import { useQuery } from "@apollo/client";
import { useId } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import FlashContext from "components/FlashMessages/FlashContext";
import MetaInfoList from "components/MetaInfoList";
import MetaInfoListItem from "components/MetaInfoList/Item";
import PrivateProviderRegistryTierInfo from "components/TierInfo/PrivateProviderRegistryTierInfo";
import ViewHeader from "components/ViewHeader";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import NotFoundPage from "components/error/NotFoundPage";
import { Incognito, Network, Space } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import EmptyState from "ds/components/EmptyState";
import Link from "ds/components/Link";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import useCanDeleteTerraformProvider from "shared/Terraform/access/useCanDeleteTerraformProvider";
import useCanUpdateTerraformProvider from "shared/Terraform/access/useCanUpdateTerraformProvider";
import { getDocsUrl } from "utils/getDocsUrl";

import { showProviderDeleteConfirmation } from "./ProviderDeleteConfirmation";
import { showProviderDetailsDrawer } from "./ProviderDetailsDrawer";
import { showProviderDrawer } from "./ProviderDrawer";
import ProviderVersion from "./ProviderVersion";
import { FILTERS_ORDER_SETTINGS_KEY_PROVIDERS } from "./constants";
import { GET_PROVIDER_WITH_VERSIONS } from "./gql";
import styles from "./styles.module.css";
import useDeleteProvider from "./useDeleteProvider";

const Provider = () => {
  const { providerId } = useParams<{ providerId: string }>();
  const { reportSuccess, reportError } = useTypedContext(FlashContext);
  const navigate = useNavigate();
  const { sharingModulesBetweenSpaces } = useTypedFlags();
  const showNewNavigation = sharingModulesBetweenSpaces;

  const handleOpenUpdateProviderDrawer = () => {
    showProviderDrawer({
      provider,
    });
  };

  const [deleteProvider] = useDeleteProvider({
    refetchQueries: ["GetProviders"],
  });

  const handleDeleteProvider = () => {
    if (providerId) {
      deleteProvider({ id: providerId }, (data) => {
        if (data?.terraformProviderDelete?.deleted) {
          navigate("/providers");
          reportSuccess({
            message: `Terraform provider "${data.terraformProviderDelete.id}" was successfully deleted`,
          });
        } else {
          reportError({
            message: "Something went wrong while deleting Terraform provider, please try again.",
          });
        }
      });
    }
  };

  const handleDeleteProviderConfirmation = () => {
    if (providerId) {
      showProviderDeleteConfirmation({
        providerId,
        onConfirm: handleDeleteProvider,
      });
    }
  };

  const { error, loading, data } = useQuery(
    GET_PROVIDER_WITH_VERSIONS,
    {
      variables: {
        id: providerId!,
      },
      nextFetchPolicy: "cache-first",
      skip: !providerId,
    }
    // APOLLO CLIENT UPDATE
  );

  const provider = data?.terraformProvider;

  const handleOpenDetailsDrawer = () => {
    if (!provider) {
      return;
    }

    showProviderDetailsDrawer({
      provider,
      onEdit: handleOpenUpdateProviderDrawer,
    });
  };

  const canUpdateProvider = useCanUpdateTerraformProvider()(provider);
  const canDeleteProvider = useCanDeleteTerraformProvider()(provider);

  useBreadcrumbs(
    [
      ...(!showNewNavigation
        ? [
            {
              title: "Terraform registry",
              link: getBreadcrumbsBackUrl("/modules", FILTERS_ORDER_SETTINGS_KEY_PROVIDERS),
            },
          ]
        : []),
      {
        title: "Providers",
        link: getBreadcrumbsBackUrl("/providers", FILTERS_ORDER_SETTINGS_KEY_PROVIDERS),
      },
      {
        title: provider?.id || "",
      },
    ],
    [provider?.id]
  );

  useTitle(`Provider · ${provider?.id}`);

  const ErrorContent = useErrorHandle(error);

  const versionTitle = useId();

  const isEditProviderEnabled = canUpdateProvider.result || canUpdateProvider.message;
  const isDeleteProviderEnabled = canDeleteProvider.result || canDeleteProvider.message;

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !provider?.id) {
    return <PageLoading />;
  }

  if (!provider?.id) {
    return <NotFoundPage />;
  }

  return (
    <>
      <ViewHeader>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>{provider.id}</ViewHeaderTitle>
          </ViewHeaderWrapper>
          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <Button variant="secondary" onPress={handleOpenDetailsDrawer}>
              Details
            </Button>
            {(isEditProviderEnabled || isDeleteProviderEnabled) && (
              <DropdownMenuEllipsis tooltip="Provider actions" dotsSize="medium">
                {isEditProviderEnabled && (
                  <DropdownMenuItem
                    isDisabled={!canUpdateProvider.result}
                    tooltip={canUpdateProvider.message}
                    isTooltipActive={!canUpdateProvider.result}
                    onAction={handleOpenUpdateProviderDrawer}
                  >
                    Edit
                  </DropdownMenuItem>
                )}
                {isDeleteProviderEnabled && (
                  <DropdownMenuItem
                    isDisabled={!canDeleteProvider.result}
                    tooltip={canDeleteProvider.message}
                    isTooltipActive={!canDeleteProvider.result}
                    onAction={handleDeleteProviderConfirmation}
                    danger
                  >
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuEllipsis>
            )}
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>

        <ViewHeaderScrollCollapse>
          <MetaInfoList>
            <MetaInfoListItem
              icon={Space}
              linkText={provider.spaceDetails.name}
              href={`/spaces/${provider.space}`}
            />
            <MetaInfoListItem icon={provider?.public ? Network : Incognito}>
              {provider.public ? "Public" : "Private"}
            </MetaInfoListItem>
            {provider.latestVersionNumber && (
              <MetaInfoListItem>{provider.latestVersionNumber}</MetaInfoListItem>
            )}
          </MetaInfoList>
        </ViewHeaderScrollCollapse>
      </ViewHeader>
      <PrivateProviderRegistryTierInfo type="callout" />
      <Box direction="column" grow="1" fullWidth>
        <PageInfoHeader title="Versions" titleId={versionTitle} />

        {!loading && provider.versions.length === 0 && (
          <EmptyState
            title="You have not published any versions of this provider"
            caption={
              <>
                Publishing is normally done through a CI/CD system, but you can also do it manually
                from your workstation. You can learn more about publishing Terraform providers{" "}
                <Link
                  size="small"
                  href={getDocsUrl("/vendors/terraform/provider-registry#publishing-a-provider")}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </Link>
                .
              </>
            }
          />
        )}

        <Box
          direction="column"
          gap="large"
          className={styles.list}
          role="list"
          aria-labelledby={versionTitle}
        >
          {provider.versions.map((version) => (
            <ProviderVersion key={version.id} version={version} provider={provider} />
          ))}
        </Box>
      </Box>
    </>
  );
};

export default Provider;
